import { FC, useState } from 'react';
import toast from 'react-hot-toast';
import { WrapInLayout } from '../layouts';
import { getErrorFromCatch } from '../api/api';
import { IPageParams } from '../api/IApi';
import { orderApi } from '../api/order.api';
import { msg } from '../msg';
import { downloadBlob } from '../utils/downloadBlob';
import OrdersReportList from '../components/orders/reports/OrdersReportList';
import { LATE_COLUMNS, lateRow } from '../components/orders/reports/reportColumns';
import { IDateRange, presetRange } from '../components/orders/orderDatePresets';
import { readPageSize } from '../components/common/table/tablePageSize';

const VIEW = 'late-orders';

const LateOrdersPage: FC = () => {
  const [range, setRange] = useState<IDateRange>(() => presetRange('month'));
  const [pageParams, setPageParams] = useState<IPageParams>(() => ({
    page: 0,
    size: readPageSize(VIEW, 30),
    q: '',
    // -- Not sent, and the headers are not sortable: the endpoint is a native
    // -- query with its own ORDER BY. Kept only because IPageParams requires
    // -- the field.
    sort: '',
    sortDir: 'desc',
  }));

  // --
  // -- createdFrom and createdTill are required by this endpoint, unlike the
  // -- unshipped one - the range filter always supplies both.
  // --
  const { data: orders, isFetching, isError, error } = orderApi.useGetLateOrdersQuery({
    page: pageParams.page,
    size: pageParams.size,
    createdFrom: range.createdFrom,
    createdTill: range.createdTill,
  });

  const [downloadCsv, { isFetching: isDownloading }] =
    orderApi.useLazyDownloadLateOrdersCsvQuery();

  const handleDownload = async () => {
    const result = await downloadCsv({
      createdFrom: range.createdFrom,
      createdTill: range.createdTill,
    });

    if (result.data) {
      downloadBlob(result.data, `late-orders-${new Date().toISOString().slice(0, 10)}.csv`);
      return;
    }
    toast.error(`${msg('load-orders-filed')} ${getErrorFromCatch(result.error)}`);
  };

  return (
    <WrapInLayout title="Late Orders" layout="dashboard">
      <h1 className="text-xl font-semibold mb-3">Late Orders report</h1>
      <OrdersReportList
        columns={LATE_COLUMNS}
        row={lateRow}
        orders={orders}
        isFetching={isFetching}
        isError={isError}
        error={error}
        range={range}
        onRangeChange={(next) => {
          setRange(next);
          setPageParams((prev) => ({ ...prev, page: 0 }));
        }}
        pageParams={pageParams}
        onPageParamsChange={(next) => setPageParams((prev) => ({ ...prev, ...next }))}
        storageKey={VIEW}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
    </WrapInLayout>
  );
};

export default LateOrdersPage;
